import axios from 'axios';
import api from './api';

/**
 * Resizes an image to fit within specified dimensions while maintaining aspect ratio
 * @param file The original image file
 * @param maxWidth Maximum width in pixels
 * @param maxHeight Maximum height in pixels
 * @param quality JPEG quality (0.0 to 1.0)
 * @returns Promise with the resized image as a Blob
 */
const resizeImage = (file: File, maxWidth: number, maxHeight: number, quality: number): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw the resized image
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to resize image'));
          }
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for resizing'));
    };

    img.src = URL.createObjectURL(file);
  });
};

// S3 configuration
const S3_CONFIG = {
  bucket: process.env.REACT_APP_S3_BUCKET || 'myeventshop-images',
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  maxFileSize: 5 * 1024 * 1024, // 5MB
  allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  // Image resizing configuration
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.8, // JPEG quality (0.0 to 1.0)
  maxResizedFileSize: 2 * 1024 * 1024, // 2MB max after resizing
};

/**
 * Service for handling AWS S3 operations
 */
export const s3Service = {
  /**
   * Uploads an image to AWS S3 and returns the URL
   * @param file The file to upload
   * @param folder Optional folder path within the bucket
   * @param onProgress Optional callback for upload progress
   * @returns Promise with the S3 URL of the uploaded image
   */
  uploadImage: async (
    file: File, 
    folder: string = 'events', 
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    try {
      // Validate file type
      if (!S3_CONFIG.allowedFileTypes.includes(file.type)) {
        throw new Error(`Invalid file type. Allowed types: ${S3_CONFIG.allowedFileTypes.join(', ')}`);
      }
      
      // Validate file size
      if (file.size > S3_CONFIG.maxFileSize) {
        throw new Error(`File too large. Maximum size: ${S3_CONFIG.maxFileSize / (1024 * 1024)}MB`);
      }

      // Resize image if needed
      let processedFile: File | Blob = file;
      let fileName = file.name;
      
      // Check if image needs resizing (only for images, not other file types)
      if (file.type.startsWith('image/')) {
        try {
          const resizedBlob = await resizeImage(
            file, 
            S3_CONFIG.maxWidth, 
            S3_CONFIG.maxHeight, 
            S3_CONFIG.quality
          );
          
          // Check if resized file is smaller than original
          if (resizedBlob.size < file.size) {
            processedFile = resizedBlob;
            // Update file name to indicate it's been processed
            const nameParts = file.name.split('.');
            const extension = nameParts.pop();
            const baseName = nameParts.join('.');
            fileName = `${baseName}_resized.${extension}`;
          }
          
          // Validate resized file size
          if (resizedBlob.size > S3_CONFIG.maxResizedFileSize) {
            throw new Error(`Resized file still too large. Maximum size: ${S3_CONFIG.maxResizedFileSize / (1024 * 1024)}MB`);
          }
        } catch (resizeError) {
          console.warn('Failed to resize image, uploading original:', resizeError);
          // Continue with original file if resizing fails
        }
      }
      
      // First, get a pre-signed URL from our backend
      const response = await api.get(`/s3/presigned-url`, {
        params: {
          fileName: fileName,
          fileType: processedFile instanceof File ? processedFile.type : file.type,
          folder
        }
      });

      // Validate response data
      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      const { uploadUrl, key } = response.data;

      // Validate the upload URL
      if (!uploadUrl) {
        throw new Error('Failed to get upload URL from server');
      }

      // Validate the key
      if (!key) {
        throw new Error('Failed to get file key from server');
      }

      // Upload the file directly to S3 using the pre-signed URL
      await axios.put(uploadUrl, processedFile, {
        headers: {
          'Content-Type': processedFile instanceof File ? processedFile.type : file.type,
          'Access-Control-Allow-Origin': '*'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      });

      // Construct the public URL from the key
      const publicUrl = `https://myeventshop-images.s3.eu-west-1.amazonaws.com/${key}`;
      return publicUrl;
    } catch (error: any) {
      console.error('Error uploading image to S3:', error);
      
      // If it's our custom error, throw it directly
      if (error.message && (
        error.message.includes('Failed to get upload URL') ||
        error.message.includes('Failed to get file key') ||
        error.message.includes('Invalid response')
      )) {
        throw error;
      }
      
      // Provide more specific error messages
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 403) {
          throw new Error('Permission denied. Check your AWS credentials and bucket permissions.');
        } else if (error.response.status === 404) {
          throw new Error('S3 service not found. Please check your backend configuration.');
        } else {
          throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        }
      } else if (error.request) {
        // The request was made but no response was received
        throw new Error('Network error. Please check your internet connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        throw error;
      }
    }
  },

  /**
   * Deletes an image from AWS S3
   * @param fileUrl The URL of the file to delete
   * @returns Promise with the response from the delete operation
   */
  deleteImage: async (fileUrl: string): Promise<any> => {
    try {
      // Extract the key from the URL
      const key = fileUrl.split('/').slice(3).join('/');
      
      // Call our backend to delete the file
      const response = await api.delete(`/s3/delete-file`, {
        params: { key }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error deleting image from S3:', error);
      
      if (error.response) {
        if (error.response.status === 403) {
          throw new Error('Permission denied. Check your AWS credentials and bucket permissions.');
        } else {
          throw new Error(`Server error: ${error.response.status} - ${error.response.data.message || 'Unknown error'}`);
        }
      } else if (error.request) {
        throw new Error('Network error. Please check your internet connection.');
      } else {
        throw error;
      }
    }
  },
  
  /**
   * Gets the configuration for S3
   * @returns The S3 configuration object
   */
  getConfig: () => {
    return { ...S3_CONFIG };
  }
};

export default s3Service; 